import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  useTheme,
  Button,
  Avatar,
  TextField,
  InputAdornment,
  Stack,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  IconButton,
  alpha,
  Divider,
  List,
  Checkbox,
  FormGroup,
  FormControlLabel,
} from '@mui/material';
import {
  MoreHoriz as MoreHorizIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridRowsProp, GridRowParams } from '@mui/x-data-grid';
import { gql, useQuery } from '@apollo/client';
import UserForm from './UserForm';
import EditUserForm from './EditUser';
import DeleteUserDialog, { UserToDeleteDetails } from './DeleteUser';
import ActivityLog from './ActivityLog';
import SuccessToastAddUser from './SuccessToastAddUser';

// Interfaces and other setup code
interface NewUserFormData {
  user: { name: string; email: string; };
  role: string;
}

interface UserManagementPageProps {
  currentTenantId: string | null;
}

interface UserDataForEdit {
  id: string;
  name: string;
  email: string;
  currentRole: string;
}

interface RawTenantMembership {
    id: string;
    user: {
        id: string;
        name: string;
        email: string;
        photo?: string;
        lastLogin?: string;
    };
    roleInTenant: string;
    addedBy?: {
        id:string;
        name: string;
        photo?: string;
    };
    addedAt: string;
}

interface RawTenantInvitation {
  id: string;
  email: string;
  nameHint?: string;
  roleInTenant: string;
  status: 'PENDING' | 'ACCEPTED' | 'EXPIRED';
  sentAt: string;
  expiresAt: string;
  invitedBy?: {
    id: string;
    name: string;
    photo?: string;
  };
  acceptedByUser?: {
    id: string; name?: string; email: string; photo?: string;
  };
}

const GET_TENANT_MEMBERS = gql`
  query GetTenantMembers($tenantId: ID!) {
    usersByTenant(tenantId: $tenantId) {
      id
      user {
        id
        name
        email
        photo
        lastLogin
      }
      roleInTenant
      addedBy {
        id
        name
        photo
      }
      addedAt
    }
  }
`;

const GET_TENANT_INVITATIONS = gql`
  query GetTenantInvitations($tenantId: ID!, $statuses: [InvitationStatus!]) {
    tenantInvitations(tenantId: $tenantId, statuses: $statuses) {
      id
      email
      nameHint
      roleInTenant
      status
      sentAt
      expiresAt
      invitedBy {
        id
        name
        photo
      }
      acceptedByUser {
        id
        name
        photo
      }
    }
  }
`;

interface DisplayUser {
  id: string;
  type: 'membership' | 'invitation';
  userName: string;
  userEmail: string;
  userPhoto?: string;
  role: string;
  status: "Accepted" | "Pending" | "Invited (Accepted)" | "Invited (Expired)";
  addedByName?: string;
  addedByPhoto?: string;
  addedOn: string;
  lastLogin?: string;
  actualUserId?: string;
  membershipId?: string;
  invitationId?: string;
}


const colors = {
  pageBg: '#F9FAFB',
  cardBg: '#FFFFFF',
  primaryText: '#101828',
  secondaryText: '#667085',
  headerText: '#475467',
  placeholderText: '#667085',
  filterInputBorder: '#D0D5DD',
  adminRoleDot: '#FF9900',
  editorRoleDot: '#0064FF',
  viewerRoleDot: '#6C737F',
  buttonBlue: '#0064FF',
  activityLogIcon: '#667085',
  activityLogText: '#344054',
  searchInputBg: '#0000000A',
};


const formatDate = (isoString: string | null | undefined): string => {
  if (!isoString) return 'N/A';
  try {
    const date = new Date(isoString);
    const formattedDate = date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    const formattedTime = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${formattedDate} • ${formattedTime}`;
  } catch (e) {
    console.error("Error formatting date:", isoString, e);
    return 'Invalid Date';
  }
};

const formatRole = (role: string | null | undefined): string => {
  if (!role) return 'N/A';
  const lowerRole = role.toLowerCase();
  return lowerRole.charAt(0).toUpperCase() + lowerRole.slice(1);
};

// Simplified ErrorDisplay component, as it's only used in one context now
const ErrorDisplay = ({ onRefresh }: { onRefresh: () => void; }) => (
  <Box sx={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    p: 3,
    height: '100%',
    mt: 13, 
  }}>
    <img src="/usmerror.svg" alt="Error" style={{ width: '150px', marginBottom: '24px' }} />
    <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
      Unable to Display Data
    </Typography>
    <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '400px', mb: 3 }}>
      We're currently experiencing a temporary issue. Please try refreshing or check back shortly.
    </Typography>
     <Button
            variant="text"
            onClick={onRefresh}
            sx={{
                textTransform: 'none',
                color: '#0064FF',
                fontWeight: 500,
                fontFamily:'Inter',
                lineHeight:'21px',
                fontSize:'14px',
            }}
            >
            Refresh Page
            </Button>
  </Box>
);


const UserManagementPage: React.FC<UserManagementPageProps> = ({ currentTenantId }) => {
  const theme = useTheme();
  const NEW_ROW_HEIGHT_IN_REM = 5.6875;
  const FOOTER_HEIGHT_IN_REM = '3.5rem';

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [tempSelectedRoles, setTempSelectedRoles] = useState<string[]>([]);
  const [allDisplayUsers, setAllDisplayUsers] = useState<DisplayUser[]>([]);
  const [rawTenantMemberships, setRawTenantMemberships] = useState<RawTenantMembership[]>([]);

  const [filteredUserRows, setFilteredUserRows] = useState<GridRowsProp>([]);
  const [anchorElRole, setAnchorElRole] = useState<null | HTMLElement>(null);
  const [isUserFormOpen, setIsUserFormOpen] = useState(false);

  const [anchorElActions, setAnchorElActions] = useState<null | HTMLElement>(null);
  const [selectedMembershipId, setSelectedMembershipId] = useState<string | null>(null);
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);

  const [isActivityLogOpen, setIsActivityLogOpen] = useState(false);

  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const [isEditUserFormOpen, setIsEditUserFormOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState<UserDataForEdit | null>(null);

  const [isDeleteUserDialogOpen, setIsDeleteUserDialogOpen] = useState(false);
  const [userForDeletion, setUserForDeletion] = useState<UserToDeleteDetails | null>(null);

  const location = useLocation();
  const navigate = useNavigate();

  const roleOptions = ['Admin', 'Editor', 'Viewer'];

  const { loading: membersLoading, error: membersError, data: membersData, refetch: refetchMembers } = useQuery(GET_TENANT_MEMBERS, {
    variables: { tenantId: currentTenantId },
    skip: !currentTenantId,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const { loading: invitationsLoading, error: invitationsError, data: invitationsData, refetch: refetchInvitations } = useQuery(GET_TENANT_INVITATIONS, {
    variables: { tenantId: currentTenantId, statuses: ['PENDING', 'ACCEPTED', 'EXPIRED'] },
    skip: !currentTenantId,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const loading = membersLoading || invitationsLoading;
  const error = membersError || invitationsError;

  const handleOpenActionsMenu = (event: React.MouseEvent<HTMLElement>, rowId: string) => {
    setAnchorElActions(event.currentTarget);
    setSelectedMembershipId(rowId);
  };

  const handleCloseActionsMenu = () => {
    setAnchorElActions(null);
    setSelectedMembershipId(null);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('action') === 'add-user') {
      setIsUserFormOpen(true);
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);


  const columns: GridColDef[] = [
    {
      field: 'user',
      headerName: 'Name',
      flex: 1.5,
      sortable: false,
      minWidth: 250,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', py: 0.5 }}>
          <Avatar src={params.value?.imageUrl} sx={{ width: '36px', height: '36px', mr: 1.5, fontFamily: 'Inter', flexShrink: 0 }}>
            {params.value?.name?.substring(0, 1)}
          </Avatar>
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', flexGrow: 1, overflow: 'hidden' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '14px', fontFamily: 'Inter', color: '#333333', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {params.value?.name || 'N/A'}
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 400, fontSize: '12px', fontFamily: 'Inter', color: '#333333', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {params.value?.email || 'N/A'}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'role',
      headerName: 'Role',
      sortable: false,
      flex: 0.75,
      minWidth: 120,
      renderCell: (params) => {
        let dotColor: string = colors.viewerRoleDot;
        const roleValue = params.value as string;
        if (roleValue === 'Admin') dotColor = colors.adminRoleDot;
        else if (roleValue === 'Editor') dotColor = colors.editorRoleDot;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: '8px', height: '8px', borderRadius: '50%', bgcolor: dotColor, flexShrink: 0 }} />
            <Typography variant="body2" sx={{ fontFamily: 'Inter', color: colors.primaryText, fontWeight: 400, fontSize: '14px' }}>
              {roleValue}
            </Typography>
          </Box>
        );
      }
    },
    {
      field: 'status',
      headerName: 'Status',
      sortable: false,
      flex: 0.8,
      minWidth: 150,
      renderCell: (params) => {
        const statusValue = params.value as DisplayUser['status'];
        let color = colors.secondaryText;
        if (statusValue === 'Accepted') color = theme.palette.success.main;
        else if (statusValue === 'Pending') color = theme.palette.warning.main;
        else if (statusValue === 'Invited (Accepted)') color = theme.palette.info.main;
        else if (statusValue === 'Invited (Expired)') color = theme.palette.error.light;

        return <Typography variant="body2" sx={{ fontSize:'14px', fontFamily: 'Inter', color, fontWeight: 400 }}>{statusValue}</Typography>;
      }
    },
    {
      field: 'addedBy',
      headerName: 'Added By',
      sortable: false,
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        params.value?.name && params.value.name !== 'N/A' ? (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Avatar src={params.value.photo} sx={{ width: '20px', height: '20px', mr: 1.5, fontFamily: 'Inter', flexShrink: 0 }}>
              {params.value.name.substring(0, 1)}
            </Avatar>
            <Typography variant="body2" sx={{ fontSize: '14px',fontWeight: 400, fontFamily: 'Inter', color: colors.primaryText, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {params.value.name}
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" sx={{ fontFamily: 'Inter', color: colors.secondaryText }}>
            N/A
          </Typography>
        )
      )
    },
    {
      field: 'addedOn',
      headerName: 'Added On',
      sortable: false,
      flex: 1,
      minWidth: 170,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontFamily: 'Inter', color: colors.primaryText, fontWeight: 400, fontSize: '14px' }}>
          {params.value}
        </Typography>
      )
    },
      
    {
      field: 'lastLogin',
      headerName: 'Last Login',
      sortable: false,
      flex: 1,
      minWidth: 170,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontFamily: 'Inter', color: colors.primaryText, fontWeight: 400, fontSize: '14px' }}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: '',
      sortable: false,
      filterable: false,
      width: 40,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const rowData = params.row as any;
        const canShowActions = rowData._type === 'membership';
        const isActionVisible = canShowActions && (hoveredRowId === params.row.id || selectedMembershipId === params.row.id);

        return (
          <IconButton
            aria-label="actions"
            size="small"
            onClick={(event) => handleOpenActionsMenu(event, params.row.id as string)}
            sx={{
              visibility: isActionVisible ? 'visible' : 'hidden',
              ...(isActionVisible && {
                backgroundColor: '#FFFFFF',
                width: '32px',
                height: '32px',
                borderRadius: '0.375rem',
                border: `1px solid ${theme.palette.divider}`,
                padding: theme.spacing(0.5),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.grey[500], 0.2),
                }
              }),
            }}
          >
            <MoreHorizIcon sx={{ fontSize: '18px' }} />
          </IconButton>
        );
      }
    },
  ];

  useEffect(() => {
    if (!membersLoading && !invitationsLoading && (membersData || invitationsData)) {
      const currentMemberships: RawTenantMembership[] = membersData?.usersByTenant || [];
      const currentInvitations: RawTenantInvitation[] = invitationsData?.tenantInvitations || [];

      setRawTenantMemberships(currentMemberships);

      const memberDisplayUsers: DisplayUser[] = currentMemberships.map((mem: RawTenantMembership) => ({
        id: mem.id,
        type: 'membership',
        userName: mem.user?.name || 'N/A',
        userEmail: mem.user?.email || 'N/A',
        userPhoto: mem.user?.photo,
        role: formatRole(mem.roleInTenant),
        status: "Accepted",
        addedByName: mem.addedBy?.name,
        addedByPhoto: mem.addedBy?.photo,
        addedOn: formatDate(mem.addedAt),
        lastLogin: formatDate(mem.user?.lastLogin),
        actualUserId: mem.user?.id,
        membershipId: mem.id,
        invitationId: undefined,
      }));

      const memberEmails = new Set(memberDisplayUsers.map(m => m.userEmail.toLowerCase()));

      const invitationDisplayUsers: DisplayUser[] = currentInvitations
        .filter(inv => !memberEmails.has(inv.email.toLowerCase()))
        .map((inv: RawTenantInvitation) => {
          let statusString: DisplayUser['status'] = "Pending";
          if (inv.status === 'ACCEPTED') statusString = "Invited (Accepted)";
          else if (inv.status === 'EXPIRED') statusString = "Invited (Expired)";

          return {
            id: inv.id,
            type: 'invitation',
            userName: inv.nameHint || inv.email.split('@')[0],
            userEmail: inv.email,
            userPhoto: inv.acceptedByUser?.photo,
            role: formatRole(inv.roleInTenant),
            status: statusString,
            addedByName: inv.invitedBy?.name,
            addedByPhoto: inv.invitedBy?.photo,
            addedOn: formatDate(inv.sentAt),
            lastLogin: undefined,
            actualUserId: inv.acceptedByUser?.id,
            membershipId: undefined,
            invitationId: inv.id,
          };
        });

      setAllDisplayUsers([...memberDisplayUsers, ...invitationDisplayUsers]);

    } else if (!membersLoading && !invitationsLoading && !membersError && !invitationsError) {
      setAllDisplayUsers([]);
      setRawTenantMemberships([]);
    }
  }, [membersData, invitationsData, membersLoading, invitationsLoading, membersError, invitationsError]);

  useEffect(() => {
    let newFilteredRows = allDisplayUsers.map(du => ({
      id: du.id,
      user: { name: du.userName, email: du.userEmail, imageUrl: du.userPhoto },
      role: du.role,
      status: du.status,
      addedBy: du.addedByName ? { name: du.addedByName, photo: du.addedByPhoto } : { name: 'N/A', photo: null },
      addedOn: du.addedOn,
      lastLogin: du.lastLogin || 'N/A',
      _type: du.type,
      _membershipId: du.membershipId,
      _invitationId: du.invitationId,
      _actualUserId: du.actualUserId,
    }));

    if (searchTerm.trim()) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      newFilteredRows = newFilteredRows.filter(row =>
        (row.user?.name?.toLowerCase().includes(lowerSearchTerm)) ||
        (row.user?.email?.toLowerCase().includes(lowerSearchTerm)) ||
        (row.addedBy?.name !== 'N/A' && row.addedBy?.name?.toLowerCase().includes(lowerSearchTerm))
      );
    }
    
    if (selectedRoles.length > 0) {
      newFilteredRows = newFilteredRows.filter(row => selectedRoles.includes(row.role));
    }
    setFilteredUserRows(newFilteredRows);
  }, [searchTerm, selectedRoles, allDisplayUsers]);

  const handleRoleButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    setTempSelectedRoles([...selectedRoles]);
    setAnchorElRole(event.currentTarget);
  };

  const handleCloseRoleMenu = () => {
    setAnchorElRole(null);
  };

  const handleRoleCheckboxChange = (role: string) => {
    setTempSelectedRoles(prev =>
      prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
    );
  };

  const handleApplyRoles = () => {
    setSelectedRoles([...tempSelectedRoles]);
    handleCloseRoleMenu();
  };

  const handleClearRoles = () => {
    setTempSelectedRoles([]);
  };

  const handleOpenUserForm = () => setIsUserFormOpen(true);
  const handleCloseUserForm = () => setIsUserFormOpen(false);

  const handleUserAdded = (newUserData: NewUserFormData) => {
    refetchMembers();
    refetchInvitations();
    setToastMessage(`User ${newUserData.user.name} added and invitation sent successfully.`);
    setShowSuccessToast(true);
  };

  const handleOpenEditUserForm = (rowId: string) => {
    const displayUserEntry = allDisplayUsers.find(du => du.id === rowId);

    if (displayUserEntry && displayUserEntry.type === 'membership' && displayUserEntry.membershipId) {
      setUserToEdit({
        id: displayUserEntry.membershipId,
        name: displayUserEntry.userName,
        email: displayUserEntry.userEmail,
        currentRole: displayUserEntry.role,
      });
      setIsEditUserFormOpen(true);
    } else {
      console.error("User not found for editing or not a member:", rowId);
      setToastMessage('Error: Could not find member details to edit.');
      setShowSuccessToast(true);
    }
    handleCloseActionsMenu();
  };

  const handleUserRoleUpdated = (membershipId: string, newRole: string) => {
    refetchMembers();
    refetchInvitations();
    setToastMessage(`User role has been successfully updated to ${newRole}.`);
    setShowSuccessToast(true);
    setIsEditUserFormOpen(false);
    setUserToEdit(null);
  };

  const handleOpenDeleteDialog = (rowId: string) => {
    const displayUserEntry = allDisplayUsers.find(du => du.id === rowId);

    if (displayUserEntry && displayUserEntry.type === 'membership' && displayUserEntry.membershipId) {
      const member = rawTenantMemberships.find(m => m.id === displayUserEntry.membershipId);
      if (member && member.user) {
        setUserForDeletion({
          actualUserId: member.user.id,
          name: member.user.name,
          email: member.user.email,
          avatarUrl: member.user.photo,
          role: formatRole(member.roleInTenant),
        });
        setIsDeleteUserDialogOpen(true);
      } else {
        console.error("Full member details not found in rawTenantMemberships for deletion:", displayUserEntry.membershipId);
        setToastMessage('Error: Could not find full member details for deletion.');
        setShowSuccessToast(true);
      }
    } else {
      console.error("User not found for deletion or not a member:", rowId);
      setToastMessage('Error: Could not find member details for deletion.');
      setShowSuccessToast(true);
    }
    handleCloseActionsMenu();
  };

  const handleUserSuccessfullyDeleted = (deletedUserId: string) => {
    refetchMembers();
    refetchInvitations();
    const deletedUser = userForDeletion;
    setToastMessage(`User ${deletedUser?.name || 'ID: ' + deletedUserId} has been successfully deleted.`);
    setShowSuccessToast(true);
    setIsDeleteUserDialogOpen(false);
    setUserForDeletion(null);
  };

  const handleCloseToast = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setShowSuccessToast(false);
  };

  const handleOpenActivityLog = () => setIsActivityLogOpen(true);
  const handleCloseActivityLog = () => setIsActivityLogOpen(false);

  const handleRefresh = () => {
    refetchMembers();
    refetchInvitations();
  };

  const getRowClassName = (params: GridRowParams) => {
    if (params.id === selectedMembershipId) {
      return 'active-row-styling';
    }
    return '';
  };

  if (!currentTenantId) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', p: 3 }}><Paper sx={{p:3}}><Typography variant="h6" color="error">No Tenant Selected</Typography><Typography>Please select a tenant.</Typography></Paper></Box>;
  
  if (loading && allDisplayUsers.length === 0) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box>;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2.5), p: {xs: 2, md: 3}, backgroundColor: colors.pageBg, minHeight: 'calc(100vh - 4rem)' }}>
      <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 0 }
        }}>
        <Box>
          <Typography variant="h1" sx={{ fontFamily: 'Inter', fontWeight: 600, fontSize: '14px', color: '#333333',mb:0.5 }}>
            User Management ({filteredUserRows.length})
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '12px', color: '#666666' }}>
            Add, edit, and manage users to administer their accounts and access roles within the platform.  
          </Typography>
        </Box>
        <Button 
          variant="contained" 
          disableElevation 
          onClick={handleOpenUserForm}
          sx={{ 
            backgroundColor: colors.buttonBlue, 
            borderRadius: '0.5rem', 
            textTransform: 'none', 
            fontFamily: 'Inter', 
            fontWeight: 500, 
            fontSize: '14px', 
            color: 'white', 
            padding: '0.375rem 0.875rem', 
            height: '2.25rem', 
            '&:hover': { backgroundColor: '#0052cc' } 
          }}
        >
          Add New User
        </Button>
      </Box>

      <Paper elevation={0} sx={{
          display: 'flex',
          borderRadius: '0.5rem',
          p: '0.8125rem',
          bgcolor: colors.cardBg,
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          mb: 0
        }}>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexGrow: 1, flexWrap: 'wrap', gap: 1.5 }}>
            <TextField
                variant="outlined"
                size="small"
                placeholder="Search by user name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{
                    minWidth: '16rem',
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '0.625rem', fontFamily: 'Inter',  
                        height: '2.0625rem', backgroundColor: colors.searchInputBg, color: '#999999', 
                        fontWeight: 400,
                        fontSize: '12px',
                        '& fieldset': {
                borderColor: 'transparent',
            },
            '&:hover fieldset': {
                borderColor: 'transparent',
            },
            '&.Mui-focused fieldset': {
                borderColor: colors.buttonBlue,
            },
        },
        '& .MuiInputBase-input::placeholder': {
            color: '#999999',
            opacity: 1
        },
    }}

                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                          <IconButton onClick={() => {}} sx={{ p: 0, ml: 0 }}>
                            <img src={"/icons/search_icon.svg"} alt="Search" width="18" height="18" />
                          </IconButton>
                        </InputAdornment>
                    ),
                }}
            />
            <Button
                id="role-filter-button"
                aria-controls={Boolean(anchorElRole) ? 'role-filter-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={Boolean(anchorElRole) ? 'true' : undefined}
                variant="contained"
                onClick={handleRoleButtonClick}
                endIcon={<KeyboardArrowDownIcon sx={{ transition: 'transform 0.2s', transform: Boolean(anchorElRole) ? 'rotate(180deg)' : 'none' }} />}
                sx={{
                    textTransform: 'none', fontWeight: 400, color: 'text.primary', height: '32px',
                    borderRadius: '8px', boxShadow: 'none', border: '1px solid',
                    borderColor: Boolean(anchorElRole) || selectedRoles.length > 0 ? '#0064FF' : theme.palette.divider,
                    backgroundColor: Boolean(anchorElRole) || selectedRoles.length > 0 ? '#F3F8FE' : 'transparent',
                    '&:hover': { boxShadow: 'none', borderColor: '#0064FF' },
                }}
            >
              {selectedRoles.length === 0 ? (
                <Typography component="span" sx={{ fontSize: '14px' }}>Role</Typography>
               ) : (
                <Box component="span" sx={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                    <Typography component="span" sx={{ fontSize: '14px', fontWeight:500 }}>Role:</Typography>
                    <Typography component="span" sx={{ fontSize: '14px', fontWeight: 400 }}>{selectedRoles.join(' & ')}</Typography>
                </Box>
               )}
            </Button>
            
            <Menu
                id="role-filter-menu"
                anchorEl={anchorElRole}
                open={Boolean(anchorElRole)}
                onClose={handleCloseRoleMenu}
                MenuListProps={{ 'aria-labelledby': 'role-filter-button', sx: { p: 0 } }}
                PaperProps={{ elevation: 3, sx: { width: '229px', borderRadius: '12px', mt: '8px', overflow: 'hidden' } }}
            >
                <Box sx={{ px: '16px', pt: '12px', pb: '8px' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#666666', fontFamily: 'Inter', fontSize: '14px' }}>
                        Select Role
                    </Typography>
                </Box>
                <Divider />
                <Box sx={{ py: '2px' }}>
                    <FormGroup>
                        {roleOptions.map((role) => (
                          <FormControlLabel
                              key={role}
                              control={ <Checkbox checked={tempSelectedRoles.includes(role)} onChange={() => handleRoleCheckboxChange(role)} size="small" sx={{ '&.Mui-checked': { color: '#0064FF' } }} /> }
                              label={role}
                              sx={{ 
                                  width: '100%', m: 0, px: '16px', py: '4px', pl: 0,
                                  ...(tempSelectedRoles.includes(role) && { backgroundColor: '#F3F8FE' }) 
                              }}
                              componentsProps={{ 
                                  typography: { 
                                      sx: { 
                                          color: tempSelectedRoles.includes(role) ? '#333333' : '#666666', 
                                          fontSize: '14px' 
                                      } 
                                  } 
                              }}
                          />
                        ))}
                    </FormGroup>
                </Box>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: '6px' }}>
                    <Button onClick={handleClearRoles} sx={{ textTransform: 'none', fontSize:'14px', color: tempSelectedRoles.length > 0 ? '#333333' : '#7B7B7B', minWidth: 'auto', padding: '6px 8px' }}>
                        Clear
                    </Button>
                    <Button onClick={handleApplyRoles} sx={{ textTransform: 'none', fontSize:'14px', color: '#0064FF', minWidth: 'auto', padding: '6px 8px' }}>
                        Apply
                    </Button>
                </Box>
            </Menu>

        </Stack>
        <Button
            variant="contained"
            disableElevation
            onClick={handleOpenActivityLog}
            startIcon={<img src="/activityvector.svg" alt="Activity Log Icon" style={{ width: '13.33px', height: '13.33px' }} />}
            sx={{
                textTransform: 'none', fontFamily: 'Inter', fontSize: '14px', fontWeight: 500,
                color: colors.primaryText, backgroundColor: colors.searchInputBg, borderRadius: '0.5rem',
                height: '2.0625rem', padding: '0 1rem', boxShadow: 'none',
                '&:hover': { backgroundColor: alpha(colors.filterInputBorder, 0.7), boxShadow: 'none' },
            }}
        >
            Activity Log
        </Button>
      </Paper>

      <Paper elevation={0} sx={{ borderRadius: '0.75rem', display: 'flex', flexDirection: 'column', bgcolor: colors.cardBg, overflow: 'hidden', flexGrow: 1, minHeight: '25rem' }}>
        {error && !loading ? (
          <ErrorDisplay onRefresh={handleRefresh} />
        ) : (
          <DataGrid
            rows={filteredUserRows}
            columns={columns}
            loading={loading}
            disableColumnMenu 
            initialState={{ pagination: { paginationModel: { pageSize: 5 } }, }}
            autoHeight={false}
            pageSizeOptions={[5, 10, 20]}
            checkboxSelection={false}
            disableRowSelectionOnClick
            rowHeight={91}
            columnHeaderHeight={56}
            getRowClassName={getRowClassName}
            localeText={{
              paginationDisplayedRows: ({ from, to, count }: { from: number; to: number; count: number }) =>
                `${from} - ${to} of ${count}`,
            }}
            slotProps={{
              row: {
                onMouseEnter: (event: React.MouseEvent<HTMLDivElement>) => {
                  const rowId = event.currentTarget.getAttribute('data-id');
                  if (rowId) { setHoveredRowId(rowId); }
                },
                onMouseLeave: () => { setHoveredRowId(null); },
              },
            }}
            sx={{
              flex: 1, display: 'flex', flexDirection: 'column', border: 'none',
              '&, & div, & div div, & div div div, & div div div div': { border: 'none !important', },
              '& .MuiDataGrid-main': { backgroundColor: colors.cardBg, flexGrow: 1, overflow: 'hidden', border: 'none !important', },
              fontFamily: 'Inter',
              '& .MuiDataGrid-columnHeaders': { flexShrink: 0,  minHeight: '3.5rem !important', maxHeight: '3.5rem !important', lineHeight: '3.5rem !important' },
              '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 500, fontSize: '0.75rem', color: colors.headerText, textTransform: 'none' },
              '& .MuiDataGrid-columnHeader': { padding: '0 1.5rem', bgcolor :colors.cardBg },
              '& .MuiDataGrid-columnSeparator': { display: 'none' },
              '& .MuiDataGrid-cell': {
                borderBottom: 'none !important', minHeight: `${NEW_ROW_HEIGHT_IN_REM}rem !important`, maxHeight: `${NEW_ROW_HEIGHT_IN_REM}rem !important`,
                lineHeight: 'normal !important', padding: '0 1.5rem', display: 'flex', alignItems: 'center',
              },
              '& .MuiDataGrid-row': { borderBottom: 'none !important', },
              '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: colors.pageBg, },
              '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: colors.cardBg, },
              '& .MuiDataGrid-row:hover, & .MuiDataGrid-row.active-row-styling': { backgroundColor: `${alpha(colors.buttonBlue, 0.08)} !important`, },
              '& .MuiDataGrid-cell[data-field="actions"]': { paddingLeft: theme.spacing(0.5), paddingRight: theme.spacing(1.5), },
              '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
              '& .MuiDataGrid-footerContainer': {
                flexShrink: 0, minHeight: `${FOOTER_HEIGHT_IN_REM} !important`, 
                padding: '0 1rem', bgcolor: colors.cardBg, borderTop: `1px solid ${theme.palette.divider}`,
              },
              '& .MuiTablePagination-root': { color: colors.secondaryText, fontFamily: 'Inter', fontSize: '0.875rem' },
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontFamily: 'Inter', fontSize: '0.875rem', color: colors.secondaryText },
              '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': { width: '0.375rem', height: '0.375rem' },
              '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb': { backgroundColor: alpha(theme.palette.grey[600], 0.5), borderRadius: '0.1875rem' },
              '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
            }}
          />
        )}
      </Paper>

      {isUserFormOpen && currentTenantId && (
        <UserForm
          open={isUserFormOpen}
          onClose={handleCloseUserForm}
          onUserAdd={handleUserAdded}
          tenantId={currentTenantId}
        />
      )}

      {isEditUserFormOpen && userToEdit && (
        <EditUserForm
          open={isEditUserFormOpen}
          onClose={() => { setIsEditUserFormOpen(false); setUserToEdit(null); }}
          onUserUpdate={handleUserRoleUpdated}
          userData={userToEdit}
        />
      )}

      {isDeleteUserDialogOpen && userForDeletion && currentTenantId && (
        <DeleteUserDialog
          open={isDeleteUserDialogOpen}
          onClose={() => { setIsDeleteUserDialogOpen(false); setUserForDeletion(null); }}
          onUserDeleted={handleUserSuccessfullyDeleted}
          userToDelete={userForDeletion}
          tenantId={currentTenantId}
        />
      )}

      <ActivityLog
        open={isActivityLogOpen}
        onClose={handleCloseActivityLog}
        tenantId={currentTenantId}
      />

      <SuccessToastAddUser
        open={showSuccessToast}
        message={toastMessage}
        onClose={handleCloseToast}
      />

      <Menu
        anchorEl={anchorElActions}
        open={Boolean(anchorElActions)}
        onClose={handleCloseActionsMenu}
        MenuListProps={{ 'aria-labelledby': `actions-button-${selectedMembershipId}` }}
        PaperProps={{ sx: { borderRadius: '8px', width: '200px' } }}
      >
        <MenuItem onClick={() => {
          if (selectedMembershipId) handleOpenEditUserForm(selectedMembershipId);
          else console.error("No user selected for edit action");
        }} >
          <ListItemIcon>
            <img src="/icons/edit_icon.svg" alt="Edit" style={{ width: '12px', height: '12px', marginLeft: '2px' }} />
          </ListItemIcon>
          <ListItemText sx={{ color: '#333333', fontWeight: 400, fontSize:'14px'}}>Edit</ListItemText>
        </MenuItem>

        <Divider />

        <MenuItem onClick={() => {
            if (selectedMembershipId) handleOpenDeleteDialog(selectedMembershipId);
            else console.error("No user selected for delete action");
          }}
        >
          <ListItemIcon>
            <img src="/icons/delete_icon.svg" alt="Delete" style={{ width: '12px', height: '12px', marginLeft: '2px' }} />
          </ListItemIcon>
          <ListItemText sx={{ color: '#CA2B20', fontWeight: 400, fontSize:'12px'}} >
            Delete
          </ListItemText>
        </MenuItem>
      </Menu>
        
    </Box>
  );
};

export default UserManagementPage;